import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, MousePointerClick, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TourStep {
  id: number;
  title: string;
  content: string;
  requiredPath?: string;
  targetSelector?: string;
  /** Optional selector used only for positioning the tour card (spotlight still uses targetSelector). */
  cardAnchorSelector?: string;
  navigatesTo?: string;
  position?: "top" | "bottom" | "left" | "right" | "center" | "top-fixed";
  showBeacon?: boolean;
  spotlightPadding?: number;
  clickPrompt?: string;
  noOverlay?: boolean;
}

const tourSteps: TourStep[] = [
  // HOME PAGE - Step 1: Header (no overlay, just floating card)
  {
    id: 1,
    title: "Your Command Center",
    content: "This is your home base. Everything starts here.",
    targetSelector: "[data-tour='header']",
    position: "bottom",
    requiredPath: "/",
    noOverlay: true,
    showBeacon: true,
    spotlightPadding: 16,
    clickPrompt: "Tap to continue"
  },
  // HOME PAGE - Step 2: Today Board card (tap to navigate)
  {
    id: 2,
    title: "Let's Check Today's Tasks",
    content: "Tap the Today Board to see your daily priorities.",
    targetSelector: "[data-tour='today-card']",
    position: "bottom",
    navigatesTo: "/today-board",
    requiredPath: "/",
    showBeacon: true,
    spotlightPadding: 8,
    clickPrompt: "Tap to go to Today Board"
  },
  
  // TODAY BOARD PAGE - Step 3: Task lanes
  {
    id: 3,
    title: "Your Daily Tasks",
    content: "Here's where you track what to bring, purchase, and do today. Each column is a different work type.",
    targetSelector: "[data-tour='today-lanes']",
    position: "bottom",
    requiredPath: "/today-board",
    showBeacon: true,
    spotlightPadding: 24,
    noOverlay: true,
    clickPrompt: "Tap to continue"
  },
  // TODAY BOARD PAGE - Step 4: Add Task button
  {
    id: 4,
    title: "Quick Add",
    content: "Need to capture something fast? Use this green button to add tasks instantly.",
    targetSelector: "[data-tour='add-task']",
    position: "top-fixed",
    requiredPath: "/today-board",
    noOverlay: true
  },
  // TODAY BOARD PAGE - Step 5: Navigation bar (tap to go to Jobs)
  {
    id: 5,
    title: "Navigate the App",
    content: "Use this bar to jump between sections. Let's check out Jobs next.",
    targetSelector: "[data-tour='navigation']",
    position: "top",
    requiredPath: "/today-board",
    navigatesTo: "/jobs",
    showBeacon: true,
    spotlightPadding: 4,
    clickPrompt: "Tap Jobs to continue"
  },
  
  // JOBS PAGE - Step 6: Jobs list
  {
    id: 6,
    title: "Your Active Jobs",
    content: "Every project has its own dashboard with schedules, tasks, and progress tracking.",
    targetSelector: "[data-tour='jobs-list']",
    position: "bottom",
    requiredPath: "/jobs",
    showBeacon: true,
    spotlightPadding: 8,
    noOverlay: true,
    clickPrompt: "Tap to continue"
  },
  // JOBS PAGE - Step 7: Navigation bar (tap to go to More)
  {
    id: 7,
    title: "One More Stop",
    content: "Let's check the More menu for additional features.",
    targetSelector: "[data-tour='navigation']",
    position: "top",
    navigatesTo: "/more",
    requiredPath: "/jobs",
    showBeacon: true,
    spotlightPadding: 4,
    clickPrompt: "Tap More to continue"
  },
  
  // MORE PAGE - Step 8: Intro card at top (no spotlight)
  {
    id: 8,
    title: "Settings & Resources",
    content: "Find Procurement, Field QA, User Preferences, and more here. You can restart this tour anytime from this menu.",
    position: "top-fixed",
    requiredPath: "/more",
    noOverlay: true,
    clickPrompt: "Tap to continue"
  },
  // MORE PAGE - Step 9: Finish card (centered)
  {
    id: 9,
    title: "You're All Set!",
    content: "Explore the app and make it yours. Look for the green + button to add tasks quickly.",
    position: "center",
    requiredPath: "/more"
  }
];

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface UserTourProps {
  onComplete?: () => void;
  onSkip?: () => void;
}

export const UserTour = ({ onComplete, onSkip }: UserTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  // Check if we're on the correct path for this step
  const isOnCorrectPath = !step.requiredPath || location.pathname === step.requiredPath;

  // Get friendly page name from path
  const getPageName = (path: string) => {
    const names: Record<string, string> = {
      "/": "Home",
      "/today-board": "Today Board",
      "/jobs": "Jobs",
      "/more": "More"
    };
    return names[path] || path;
  };

  useEffect(() => {
    // For center or top-fixed positions, or if on wrong path, don't track target
    if (step.position === "center" || step.position === "top-fixed" || !isOnCorrectPath) {
      setTargetRect(null);
      return;
    }

    const updatePosition = () => {
      if (!step.targetSelector) return;

      const spotlightEl = document.querySelector(step.targetSelector);
      const anchorSelector = step.cardAnchorSelector ?? step.targetSelector;
      const anchorEl = document.querySelector(anchorSelector);

      if (!spotlightEl) {
        setTargetRect(null);
        setCardPosition({ top: 0, left: 0 });
        return;
      }

      const spotlightRect = spotlightEl.getBoundingClientRect();
      const anchorRect = (anchorEl ?? spotlightEl).getBoundingClientRect();
      const isMobile = window.innerWidth < 768;

      // Store full target rect for spotlight
      setTargetRect({
        top: spotlightRect.top,
        left: spotlightRect.left,
        width: spotlightRect.width,
        height: spotlightRect.height,
      });

      // Calculate card position based on step.position (using anchor rect)
      if (step.position === "top") {
        // Calculate where card would go
        let cardTop = anchorRect.top - 220;

        // If that would put it off-screen, position below instead
        if (cardTop < 16) {
          cardTop = anchorRect.bottom + 16;
        }

        setCardPosition({
          top: Math.max(16, cardTop),
          left: isMobile ? 16 : Math.max(16, anchorRect.left),
        });
      } else if (step.position === "bottom") {
        setCardPosition({
          top: anchorRect.bottom + 16,
          left: isMobile ? 16 : Math.max(16, Math.min(anchorRect.left, window.innerWidth - 400)),
        });
      } else {
        setCardPosition({
          top: anchorRect.bottom + 16,
          left: isMobile ? 16 : anchorRect.left,
        });
      }
    };

    // Small delay to allow page to render after navigation
    const timer = setTimeout(updatePosition, 100);
    
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [currentStep, step, isOnCorrectPath]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSpotlightClick = () => {
    if (step.navigatesTo) {
      setIsTransitioning(true);
      navigate(step.navigatesTo);
      // Wait for navigation + element detection
      setTimeout(() => setIsTransitioning(false), 300);
    }
    handleNext();
  };

  const handleNavigateToCorrectPath = () => {
    if (step.requiredPath) {
      navigate(step.requiredPath);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = tourSteps[currentStep - 1];
      // Navigate to the previous step's required path if different
      if (prevStep.requiredPath && prevStep.requiredPath !== location.pathname) {
        setIsTransitioning(true);
        navigate(prevStep.requiredPath);
        setTimeout(() => setIsTransitioning(false), 300);
      }
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('user_preferences')
          .update({ 
            tour_completed: true,
            tour_last_viewed_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
      }
      
      setIsVisible(false);
      onComplete?.();
      
      toast({
        title: "Tour completed!",
        description: "You can restart this tour anytime from the More menu.",
      });
    } catch (error) {
      console.error('Error completing tour:', error);
    }
  };

  const handleSkip = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('user_preferences')
          .update({ 
            tour_completed: true,
            tour_last_viewed_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
      }
      
      setIsVisible(false);
      onSkip?.();
    } catch (error) {
      console.error('Error skipping tour:', error);
    }
  };

  // Hide during transitions to prevent flashing
  if (!isVisible || isTransitioning) return null;

  const isCenterPosition = step.position === "center";
  const isTopFixedPosition = step.position === "top-fixed";
  const hasSpotlight = targetRect !== null && isOnCorrectPath && !step.noOverlay;
  const padding = step.spotlightPadding ?? 8;

  // If user is on wrong page, show redirect card
  if (!isOnCorrectPath && step.requiredPath) {
    return (
      <>
        {/* Solid overlay */}
        <div 
          className="fixed inset-0 bg-primary/40 z-[100] backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleSkip();
            }
          }}
        />
        
        {/* Redirect Card */}
        <Card className="fixed z-[101] shadow-2xl border-t-4 border-secondary bg-card top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Navigation className="h-5 w-5 text-secondary" />
                  <h3 className="font-heading font-bold text-lg text-foreground">
                    Let's head to {getPageName(step.requiredPath)}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The tour continues on the {getPageName(step.requiredPath)} page. Tap below to go there.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSkip}
                className="ml-2 h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Progress Bar */}
            <div className="w-full h-1.5 bg-muted rounded-full mb-4 overflow-hidden">
              <div 
                className="h-full bg-secondary transition-all duration-300"
                style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
              />
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                disabled={isFirstStep}
                className="text-accent hover:text-accent/80 hover:bg-accent/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>

              <div className="flex gap-2 items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="text-accent hover:text-accent/80 hover:bg-accent/10"
                >
                  Skip
                </Button>
                <Button
                  size="sm"
                  onClick={handleNavigateToCorrectPath}
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium"
                >
                  Take Me There
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      {/* Spotlight Overlay with SVG Mask - only show if not noOverlay */}
      {hasSpotlight ? (
        <svg 
          className="fixed inset-0 z-[100] pointer-events-auto"
          width="100%" 
          height="100%"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleSkip();
            }
          }}
        >
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white"/>
              <rect 
                x={targetRect.left - padding}
                y={targetRect.top - padding}
                width={targetRect.width + padding * 2}
                height={targetRect.height + padding * 2}
                rx="12"
                fill="black"
              />
            </mask>
          </defs>
          <rect 
            width="100%" 
            height="100%" 
            fill="hsl(222 38% 19% / 0.40)"
            mask="url(#spotlight-mask)"
            className="backdrop-blur-sm"
          />
          {/* Spotlight glow ring */}
          <rect 
            x={targetRect.left - padding - 2}
            y={targetRect.top - padding - 2}
            width={targetRect.width + padding * 2 + 4}
            height={targetRect.height + padding * 2 + 4}
            rx="14"
            fill="none"
            stroke="hsl(40 52% 66%)"
            strokeWidth="2"
            className="animate-pulse"
          />
        </svg>
      ) : !step.noOverlay && !isCenterPosition && !isTopFixedPosition ? (
        /* Solid overlay for non-spotlight steps that need overlay */
        <div 
          className="fixed inset-0 bg-primary/40 z-[100] backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleSkip();
            }
          }}
        />
      ) : isCenterPosition ? (
        /* Solid overlay for center cards */
        <div 
          className="fixed inset-0 bg-primary/40 z-[100] backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleSkip();
            }
          }}
        />
      ) : null}

      {/* Pulsing Beacon - only show if not noOverlay */}
      {step.showBeacon && targetRect && isOnCorrectPath && !step.noOverlay && (
        <div 
          className="fixed z-[102] pointer-events-none"
          style={{
            top: targetRect.top + targetRect.height / 2 - 12,
            left: targetRect.left + targetRect.width / 2 - 12,
          }}
        >
          <div className="relative w-6 h-6">
            <div className="absolute inset-0 bg-secondary rounded-full animate-tour-beacon opacity-60" />
            <div className="absolute inset-1 bg-secondary rounded-full" />
          </div>
        </div>
      )}

      {/* Clickable Spotlight Area for Interactive Steps - only when there's an overlay */}
      {targetRect && isOnCorrectPath && !step.noOverlay && (
        <div
          className="fixed z-[103] cursor-pointer transition-all duration-200 hover:bg-secondary/10 active:bg-secondary/20"
          style={{
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
            borderRadius: '12px',
          }}
          onClick={handleSpotlightClick}
          role="button"
          aria-label={step.clickPrompt || "Tap to continue"}
        />
      )}

      {/* Tour Card */}
      <Card 
        className={`fixed z-[101] shadow-2xl border-t-4 border-secondary bg-card ${
          isCenterPosition 
            ? "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md" 
            : isTopFixedPosition
            ? "top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:right-auto md:w-auto md:max-w-md"
            : "w-[calc(100vw-32px)] md:w-auto md:max-w-sm"
        }`}
        style={
          !isCenterPosition && !isTopFixedPosition ? {
            top: `${cardPosition.top}px`,
            left: `${cardPosition.left}px`,
            right: window.innerWidth < 768 ? '16px' : 'auto',
            maxWidth: window.innerWidth < 768 ? 'calc(100vw - 32px)' : '380px'
          } : {}
        }
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-heading font-bold text-lg text-foreground">
                  {step.title}
                </h3>
                <span className="text-xs text-muted-foreground font-medium px-2 py-0.5 bg-muted rounded-full">
                  {currentStep + 1}/{tourSteps.length}
                </span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {step.content}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSkip}
              className="ml-2 h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-1.5 bg-muted rounded-full mb-4 overflow-hidden">
            <div 
              className="h-full bg-secondary transition-all duration-300"
              style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
            />
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              disabled={isFirstStep}
              className="text-accent hover:text-accent/80 hover:bg-accent/10"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>

            <div className="flex gap-2 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-accent hover:text-accent/80 hover:bg-accent/10"
              >
                Skip
              </Button>
              {targetRect && isOnCorrectPath && !step.noOverlay ? (
                <div className="flex items-center gap-2 text-secondary px-3 py-1.5 bg-secondary/10 rounded-md">
                  <MousePointerClick className="h-4 w-4 animate-bounce" />
                  <span className="text-sm font-medium">{step.clickPrompt || "Tap to continue"}</span>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium"
                >
                  {isLastStep ? 'Finish' : 'Next'}
                  {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
